import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calculator, FileText, Sparkles, Code, Search } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
const categories = [{
  title: 'Financial Calculators',
  description: 'Mortgage, EMI, SIP, and other financial tools',
  icon: Calculator,
  href: '/financial',
  count: 10,
  color: 'text-blue-600 dark:text-blue-400'
}, {
  title: 'Text & Document Tools',
  description: 'Word counter, case converter, text manipulation',
  icon: FileText,
  href: '/text-tools',
  count: 10,
  color: 'text-green-600 dark:text-green-400'
}, {
  title: 'Fun & Social',
  description: 'Secret Santa, random pickers, team tools',
  icon: Sparkles,
  href: '/fun',
  count: 6,
  color: 'text-purple-600 dark:text-purple-400'
}, {
  title: 'Developer Tools',
  description: 'Password generator, JSON formatter, encoders',
  icon: Code,
  href: '/developer',
  count: 10,
  color: 'text-orange-600 dark:text-orange-400'
}];
const allTools = [
// Financial
{
  name: 'Mortgage Calculator',
  href: '/financial/mortgage-calculator',
  category: 'Financial'
}, {
  name: 'EMI Calculator',
  href: '/financial/emi-calculator',
  category: 'Financial'
}, {
  name: 'Tip Calculator',
  href: '/financial/tip-calculator',
  category: 'Financial'
}, {
  name: 'Home Loan EMI Calculator',
  href: '/financial/home-emi-calculator',
  category: 'Financial'
}, {
  name: 'Vehicle Loan EMI Calculator',
  href: '/financial/vehicle-emi-calculator',
  category: 'Financial'
},
// Text Tools
{
  name: 'Word Counter',
  href: '/text-tools/word-counter',
  category: 'Text Tools'
}, {
  name: 'Case Converter',
  href: '/text-tools/case-converter',
  category: 'Text Tools'
}, {
  name: 'Email Extractor',
  href: '/text-tools/email-extractor',
  category: 'Text Tools'
},
// Fun & Social
{
  name: 'Secret Santa',
  href: '/fun/secret-santa',
  category: 'Fun & Social'
}, {
  name: 'Random Name Picker',
  href: '/fun/random-picker',
  category: 'Fun & Social'
},
// Developer
{
  name: 'Password Generator',
  href: '/developer/password-generator',
  category: 'Developer'
}, {
  name: 'JSON Formatter',
  href: '/developer/json-formatter',
  category: 'Developer'
}];
export function HomePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const filteredTools = allTools.filter(tool => tool.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleToolClick = (href: string) => {
    navigate(href);
    setSearchQuery('');
  };
  return <div className="min-h-screen flex flex-col" data-id="element-595">
      <div className="flex-1" data-id="element-596">
        {/* Hero Section */}
        <section className="container mx-auto px-4 py-16 md:py-24" data-id="element-597">
          <div className="max-w-3xl mx-auto text-center" data-id="element-598">
            <h1 className="text-4xl md:text-6xl font-bold mb-4" data-id="element-599">
              Welcome to <span className="text-primary" data-id="element-600">Hpppy.com</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8" data-id="element-601">
              Your Swiss Army Knife of Online Tools
            </p>
            <div className="max-w-xl mx-auto relative" data-id="element-602">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" data-id="element-603" />
              <Input type="text" placeholder="Search tools... (e.g., Secret Santa, Calculator, JSON)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10" data-id="element-604" />

              {/* Search Results Dropdown */}
              {searchQuery && filteredTools.length > 0 && <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto" data-id="element-605">
                  {filteredTools.map(tool => <button key={tool.href} onClick={() => handleToolClick(tool.href)} className="w-full text-left px-4 py-3 hover:bg-muted transition-colors border-b border-border last:border-b-0" data-id="element-606">
                      <p className="font-medium" data-id="element-607">{tool.name}</p>
                      <p className="text-sm text-muted-foreground" data-id="element-608">
                        {tool.category}
                      </p>
                    </button>)}
                </div>}

              {searchQuery && filteredTools.length === 0 && <div className="absolute top-full mt-2 w-full bg-card border border-border rounded-lg shadow-lg z-50 p-4 text-center text-muted-foreground" data-id="element-609">
                  No tools found for "{searchQuery}"
                </div>}
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="container mx-auto px-4 pb-16" data-id="element-610">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-611">
            {categories.map(category => {
            const Icon = category.icon;
            return <Link key={category.href} to={category.href} className="block group" data-id="element-612">
                  <Card className="h-full transition-all hover:shadow-lg hover:border-primary/50" data-id="element-613">
                    <CardHeader className="text-center" data-id="element-614">
                      <div className={`inline-flex p-4 rounded-full bg-primary/10 mb-4 ${category.color} group-hover:scale-110 transition-transform`} data-id="element-615">
                        <Icon className="w-8 h-8" data-id="element-616" />
                      </div>
                      <CardTitle className="mb-2" data-id="element-617">{category.title}</CardTitle>
                      <CardDescription className="mb-3" data-id="element-618">
                        {category.description}
                      </CardDescription>
                      <p className="text-sm font-medium text-primary" data-id="element-619">
                        {category.count} tools available
                      </p>
                    </CardHeader>
                  </Card>
                </Link>;
          })}
          </div>
        </section>

        {/* Features Section */}
        <section className="bg-muted/50 py-16" data-id="element-620">
          <div className="container mx-auto px-4" data-id="element-621">
            <div className="max-w-4xl mx-auto" data-id="element-622">
              <h2 className="text-3xl font-bold text-center mb-12" data-id="element-623">
                Why Choose Hpppy.com?
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-624">
                <div className="text-center" data-id="element-625">
                  <div className="inline-flex p-3 rounded-full bg-primary/10 text-primary mb-4" data-id="element-626">
                    <Calculator className="w-6 h-6" data-id="element-627" />
                  </div>
                  <h3 className="font-semibold mb-2" data-id="element-628">Fast & Accurate</h3>
                  <p className="text-sm text-muted-foreground" data-id="element-629">
                    Get instant results with precision calculations
                  </p>
                </div>
                <div className="text-center" data-id="element-630">
                  <div className="inline-flex p-3 rounded-full bg-primary/10 text-primary mb-4" data-id="element-631">
                    <Sparkles className="w-6 h-6" data-id="element-632" />
                  </div>
                  <h3 className="font-semibold mb-2" data-id="element-633">Easy to Use</h3>
                  <p className="text-sm text-muted-foreground" data-id="element-634">
                    Clean interface designed for simplicity
                  </p>
                </div>
                <div className="text-center" data-id="element-635">
                  <div className="inline-flex p-3 rounded-full bg-primary/10 text-primary mb-4" data-id="element-636">
                    <Code className="w-6 h-6" data-id="element-637" />
                  </div>
                  <h3 className="font-semibold mb-2" data-id="element-638">No Sign-up Required</h3>
                  <p className="text-sm text-muted-foreground" data-id="element-639">
                    Start using tools immediately, no account needed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>;
}